import { useEffect, useState } from "react";
import { Plus, Trash2, ArrowUp, ArrowDown } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { InstagramPicker } from "./InstagramPicker";
import { FileField } from "./FileField";
import { PagesField } from "./PagesField";
import { TrackPicker } from "./music/TrackPicker";
import { TileShapePicker } from "./TileShapePicker";
import { PatreonSettings, MerchSettings } from "./support/SupportSettings";

const field = "w-full bg-[#0B0C10] border border-[#F5A524]/30 focus:border-[#F5A524] outline-none px-3 py-2.5 text-sm text-white";
const Label = ({ children }) => <span className="block font-mono text-[11px] tracking-[0.25em] text-zinc-400 mb-2">{children}</span>;
const Card = ({ title, children }) => (
  <div className="bg-[#12131A] border border-[#F5A524]/15 p-5 sm:p-6 space-y-4">
    <p className="font-display font-black uppercase text-2xl">{title}</p>
    {children}
  </div>
);
const IconBtn = ({ testid, onClick, label, children, danger }) => (
  <button type="button" data-testid={testid} onClick={onClick} aria-label={label}
    className={`p-2 border transition-colors ${danger ? "border-[#FF2A55]/40 text-[#FF2A55] hover:bg-[#FF2A55] hover:text-black" : "border-[#F5A524]/30 text-zinc-300 hover:text-[#F5A524]"}`}>{children}</button>
);
const AddBtn = ({ testid, onClick, children }) => (
  <button type="button" data-testid={testid} onClick={onClick} className="flex items-center gap-2 font-mono text-[11px] tracking-[0.2em] text-[#F5A524] border border-dashed border-[#F5A524]/40 px-4 py-2.5 hover:bg-[#F5A524]/10">
    <Plus size={14} /> {children}
  </button>
);

const move = (arr, i, d) => {
  const j = i + d;
  if (j < 0 || j >= arr.length) return arr;
  const out = [...arr];
  [out[i], out[j]] = [out[j], out[i]];
  return out;
};

export const SiteForm = () => {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/site").then((r) => setS({ ...r.data, skills: r.data.skills.map((k) => ({ ...k, toolsText: k.tools.join(", ") })) }))
      .catch((e) => toast.error(formatApiError(e)));
  }, []);

  if (!s) return <p className="font-mono text-zinc-400">LOADING...</p>;

  const set = (k, v) => setS((p) => ({ ...p, [k]: v }));
  const setItem = (k, i, patch) => set(k, s[k].map((x, j) => (j === i ? { ...x, ...patch } : x)));
  const remove = (k, i) => set(k, s[k].filter((_, j) => j !== i));

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    const body = {
      ...s,
      about_paragraphs: s.about_paragraphs.map((p) => p.trim()).filter(Boolean),
      skills: s.skills.filter((k) => k.name.trim()).map((k) => ({ name: k.name.trim(), tools: k.toolsText.split(",").map((t) => t.trim()).filter(Boolean) })),
      socials: s.socials.filter((x) => x.label.trim()).map((x) => ({ label: x.label.trim(), url: x.url.trim() })),
    };
    try {
      await api.put("/site", body);
      toast.success("SITE CONTENT SAVED");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={save} data-testid="site-form" className="space-y-6">
      <Card title="Hero images">
        <PagesField pages={s.hero_images ?? []} onChange={(v) => set("hero_images", v)} label="OPENING SECTION IMAGES"
          help="Add one or more images (portrait 4:5 works best). With 2 or more they slowly cross-fade every 6 seconds, in this order." button="UPLOAD IMAGES" />
        <label className="block"><Label>IMAGE TAG (LEAVE EMPTY TO HIDE)</Label>
          <input data-testid="site-hero-label-input" value={s.hero_label} onChange={(e) => set("hero_label", e.target.value)} className={field} />
        </label>
      </Card>

      <Card title="Works tiles">
        <TileShapePicker value={s.works_tile_shape} onChange={(v) => set("works_tile_shape", v)} />
      </Card>

      <Card title="Music player">
        <TrackPicker s={s} update={setS} />
      </Card>

      <Card title="About image">
        <FileField label="PICTURE IN THE ABOUT SECTION (PORTRAIT 4:5 WORKS BEST)" kind="image" accept="image/*" value={s.about_image_url} onChange={(v) => set("about_image_url", v)} testid="site-about-image" />
      </Card>
      <Card title="About">
        <label className="block"><Label>HEADING (LAST WORD SHOWS IN GOLD)</Label>
          <input data-testid="site-about-heading-input" value={s.about_heading} onChange={(e) => set("about_heading", e.target.value)} className={field} />
        </label>
        {s.about_paragraphs.map((p, i) => (
          <div key={i} className="flex gap-2 items-start">
            <textarea data-testid={`site-about-paragraph-${i}`} rows={3} value={p} onChange={(e) => set("about_paragraphs", s.about_paragraphs.map((x, j) => (j === i ? e.target.value : x)))} className={field} />
            <IconBtn testid={`site-about-remove-${i}`} label="Remove paragraph" danger onClick={() => remove("about_paragraphs", i)}><Trash2 size={14} /></IconBtn>
          </div>
        ))}
        <AddBtn testid="site-about-add-btn" onClick={() => set("about_paragraphs", [...s.about_paragraphs, ""])}>ADD PARAGRAPH</AddBtn>
      </Card>

      <Card title="Skills">
        {s.skills.map((k, i) => (
          <div key={i} className="grid sm:grid-cols-[1fr_1fr_auto] gap-2 items-start">
            <input data-testid={`site-skill-name-${i}`} placeholder="Skill name" value={k.name} onChange={(e) => setItem("skills", i, { name: e.target.value })} className={field} />
            <input data-testid={`site-skill-tools-${i}`} placeholder="Tags, comma separated" value={k.toolsText} onChange={(e) => setItem("skills", i, { toolsText: e.target.value })} className={field} />
            <div className="flex gap-1">
              <IconBtn testid={`site-skill-up-${i}`} label="Move up" onClick={() => set("skills", move(s.skills, i, -1))}><ArrowUp size={14} /></IconBtn>
              <IconBtn testid={`site-skill-down-${i}`} label="Move down" onClick={() => set("skills", move(s.skills, i, 1))}><ArrowDown size={14} /></IconBtn>
              <IconBtn testid={`site-skill-remove-${i}`} label="Remove skill" danger onClick={() => remove("skills", i)}><Trash2 size={14} /></IconBtn>
            </div>
          </div>
        ))}
        <AddBtn testid="site-skill-add-btn" onClick={() => set("skills", [...s.skills, { name: "", tools: [], toolsText: "" }])}>ADD SKILL</AddBtn>
      </Card>

      <Card title="Instagram — Latest">
        <InstagramPicker pinned={s.ig_pinned ?? []} setPinned={(v) => set("ig_pinned", v)}
          onlyPinned={!!s.ig_only_pinned} setOnlyPinned={(v) => set("ig_only_pinned", v)} />
      </Card>

      <Card title="Patreon">
        <PatreonSettings s={s} set={set} />
      </Card>

      <Card title="Merch / Store">
        <MerchSettings s={s} set={set} />
      </Card>

      <Card title="Contact">
        <label className="block"><Label>HEADING</Label>
          <input data-testid="site-contact-heading-input" value={s.contact_heading} onChange={(e) => set("contact_heading", e.target.value)} className={field} />
        </label>
        <label className="block"><Label>EMAIL</Label>
          <input data-testid="site-email-input" type="email" value={s.email} onChange={(e) => set("email", e.target.value)} className={field} />
        </label>
        <Label>SOCIAL LINKS (LINKS WITHOUT A URL ARE HIDDEN)</Label>
        {s.socials.map((x, i) => (
          <div key={i} className="grid sm:grid-cols-[180px_1fr_auto] gap-2">
            <input data-testid={`site-social-label-${i}`} placeholder="Label" value={x.label} onChange={(e) => setItem("socials", i, { label: e.target.value })} className={field} />
            <input data-testid={`site-social-url-${i}`} placeholder="https://..." value={x.url} onChange={(e) => setItem("socials", i, { url: e.target.value })} className={field} />
            <IconBtn testid={`site-social-remove-${i}`} label="Remove link" danger onClick={() => remove("socials", i)}><Trash2 size={14} /></IconBtn>
          </div>
        ))}
        <AddBtn testid="site-social-add-btn" onClick={() => set("socials", [...s.socials, { label: "", url: "" }])}>ADD LINK</AddBtn>
      </Card>

      <button data-testid="site-save-btn" disabled={busy}
        className="sticky bottom-4 w-full clip-tab bg-[#F5A524] text-black font-mono font-bold tracking-[0.2em] py-4 hover:bg-white disabled:opacity-60 transition-colors">
        {busy ? "SAVING..." : "SAVE SITE CONTENT"}
      </button>
    </form>
  );
};
